/**
 * SafetyScheduleConfirmSend · iter303ak-fix28 — Work Booked · T40 · Action 10
 * ──────────────────────────────────────────────────────────────────────────
 * MOCKED email + SMS to the accepted safety supplier (from Quote Sent T25).
 * Pre-fills recipient email; editable inline. Stamps safety_schedule_sent_at
 * on the parent appraisal.
 *
 * Writes to captured_data:
 *   safety_schedule_send_state: { sent_at, to_email, ok, error? }
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { sendWorkflowEmail, jobRef, signOff } from './_emailSend';
import { CheckCircle2, ShieldCheck, AlertTriangle, Send } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const isEmailValid = (e) => !!(e && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e.trim()));

export default function SafetyScheduleConfirmSend({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentAppraisalId = capturedData?.appraisal_id;
  const stampField = params.stamp_field || 'safety_schedule_sent_at';
  const plannedStart = capturedData?.work_planned_start_date || '';

  const supplierId = capturedData?.appraisal_record?.accepted_safety_supplier_id
    || capturedData?.appraisal?.accepted_safety_supplier_id
    || '';

  const [supplier, setSupplier] = useState(null);
  const [email, setEmail] = useState(value?.to_email || '');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendState, setSendState] = useState(() => value || {});

  const loadSupplier = useCallback(async () => {
    if (!supplierId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/scaffold-suppliers/${supplierId}`);
      setSupplier(data);
      if (!email && data?.email) setEmail(data.email);
    } catch {
      setSupplier(null);
    } finally {
      setLoading(false);
    }
  }, [supplierId]);   

  useEffect(() => { loadSupplier(); }, [loadSupplier]);

  const emailValid = isEmailValid(email);
  const sent = !!sendState?.sent_at;

  const send = async () => {
    if (!emailValid) return;
    setSending(true);
    try {
      await sendWorkflowEmail(api, {
        to: email.trim(),
        subject: 'Height safety booking confirmation – Roofs To Remedy Ltd',
        body: `Hi,\n\nPlease find our booking confirmation attached. Reply to confirm the date.${signOff}`,
        attachments: [{ route: `/reports/status-letter/height_safety_confirm/${parentAppraisalId}`, filename: 'Height_Safety_Confirmation' }],
        appraisalId: parentAppraisalId, event: 'height_safety_confirm',
      });
      const nowIso = new Date().toISOString();
      if (parentAppraisalId) {
        await api.patch(`/appraisals/${parentAppraisalId}`, {
          [stampField]: nowIso,
        });
      }
      const next = { sent_at: nowIso, ok: true, to_email: email.trim() };
      setSendState(next);
      if (onChange) onChange(next);
    } catch (e) {
      const err = e?.response?.data?.detail || e?.message || 'Send failed';
      const next = { error: err };
      setSendState(next);
      if (onChange) onChange(next);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="wf-safety-schedule-send">
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0 text-amber-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">Confirm Schedule with Safety Supplier</div>
            <div className="text-sm mt-1 text-amber-800">
              {params.prompt || 'Send the accepted safety supplier a schedule confirmation ~1 day before work starts.'}
            </div>
            {plannedStart && (
              <div className="text-xs mt-2 text-amber-700">
                Planned start date: <b className="font-mono">{new Date(plannedStart).toLocaleDateString()}</b>
              </div>
            )}
          </div>
        </div>
        <div className="mt-3 p-2 rounded bg-white border border-amber-200 text-[11px] text-amber-800">
          Sends a real email with the confirmation letter attached.
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500 italic p-3">Loading supplier…</div>}

      {!loading && !supplierId && (
        <div className="p-4 bg-rose-50 border border-rose-200 rounded text-sm text-rose-800">
          No accepted safety supplier found. Complete <b>Quote Sent · T25 Height Safety Contracted</b> first.
        </div>
      )}

      {!loading && supplierId && (
        <div className={`p-4 border rounded-lg ${sent ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-slate-200'}`}>
          <div className="mb-2">
            <div className="text-sm font-semibold text-slate-800">{supplier?.supplier_name || supplier?.company_name || 'Safety supplier'}</div>
            {supplier?.contact_name && <div className="text-xs text-slate-600">{supplier.contact_name}</div>}
          </div>
          <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Email <span className="text-rose-500">*</span></label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            disabled={sent} placeholder="safety@supplier.com"
            data-testid="wf-safety-schedule-email"
            className={`w-full px-2 py-1 text-xs border rounded mb-2 ${email && !emailValid ? 'border-rose-400 bg-rose-50' : 'border-slate-300'} disabled:opacity-60`} />
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] text-slate-500">
              {sent && <span className="text-emerald-700">✓ Stamped {new Date(sendState.sent_at).toLocaleString()} → {sendState.to_email}</span>}
              {sendState?.error && <span className="text-rose-700">Error: {sendState.error}</span>}
            </div>
            <button onClick={send} disabled={!emailValid || sending || sent}
              data-testid="wf-safety-schedule-send-btn"
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-1.5">
              <Send size={12} /> {sent ? 'Sent' : (sending ? 'Sending…' : 'Send')}
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={onNext} disabled={!sent}
            data-testid="wf-safety-schedule-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
